import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Layers, Shuffle, X, Users, Info, Copy, Edit, Wallet, PlusCircle } from "lucide-react";
import { BudgetPicker } from "../BudgetPicker";
import { useDraftStore } from "@/shared/store/draft";
import { useBloggers, useRecommended, useAdjacentBloggers, useCategories } from "@/shared/api/wb";
import { Avatar, InfoTip, PlatformIcon, Segmented, type Platform } from "@/shared/ui";
import {
  DEFAULT_FILTERS, PLATFORMS_META, applyFilters, facetCounts, sortBloggers, sortNiches,
  type FilterState, type SortKey,
} from "@/pages/Catalog/engine";
import { FilterSidebar } from "@/pages/Catalog/FilterSidebar";
import { ActiveFilterChips, SortBar, EmptyResults } from "@/pages/Catalog";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { StepHead } from "../StepHead";
import { BloggerCard, type CartSelection, PLATFORM_LABELS, POSEV_PLATFORMS, NATIV_PLATFORMS, type PlatformKey } from "@/features/blogger-card";

const SEEDING = new Set(["telegram", "vk", "max"]);

export function BloggersStep() {
  const { draft, branch, addBloggerFormat, removeBloggerFormat, setBranch } = useDraftStore();
  const seedingLock = branch.campaignType === "seeding";

  const categories = useCategories().data ?? [];
  const productCategoryLabel = categories.find((c) => c.id === draft.productCategory)?.label;

  const pageTitle = seedingLock ? "Каналы" : "Блогеры";
  const pageSub = seedingLock
    ? "Выберите каналы для посева. Один канал можно добавить в несколько соцсетей — каждая даёт отдельную аудиторию."
    : "Выберите блогеров, которые напишут о вашем продукте.";

  return (
    <div className="px-7 pt-6 pb-14">
      <StepHead step="02" title={pageTitle} sub={pageSub} />
      {/* TOP context bar */}
      <div className="flex items-center gap-5 px-6 py-4 mb-5 rounded-md bg-white border border-ink-12">
        <CtxItem icon={<Layers size={17} />} iconBg="var(--wb-black)" iconFg="white" k="Продукт" v={draft.productName || "—"} sub={productCategoryLabel ?? "категория не выбрана"} />
        <CtxItem
          icon={seedingLock ? <Copy size={15} strokeWidth={2.5} /> : <Edit size={15} strokeWidth={2.5} />}
          iconBg={seedingLock ? "var(--wb-lime)" : "var(--wb-pink)"} iconFg="#1C1C1C"
          k="Креатив" v={seedingLock ? "Посев" : "Натив"} sub={seedingLock ? "Готовый креатив" : "ТЗ → блогер"}
        />
        <BudgetCtxItem budget={draft.budget} />
        <div className="flex-1" />
        {seedingLock && (
          <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[12px] font-display font-semibold">
            <Info size={15} /> Посев — только Telegram, VK и MAX
          </div>
        )}
      </div>

      {/* 2-col shell */}
      <div className="grid grid-cols-[1fr_300px] gap-5 items-start">
        <div className="min-w-0">
          <div className="flex items-center justify-between mb-4">
            <Segmented<"recommended" | "adjacent" | "catalog">
              value={branch.bloggersTab}
              onChange={(v) => setBranch("bloggersTab", v)}
              options={[
                { value: "recommended", label: "Под ваш продукт 6", icon: <Sparkles size={14} /> },
                { value: "adjacent",    label: "Смежные аудитории", icon: <Shuffle size={14} /> },
                { value: "catalog",     label: "Поиск по каталогу 53К", icon: <Search size={14} /> },
              ]}
            />
          </div>

          {branch.bloggersTab === "recommended" && (
            <RecommendationsTab
              seedingLock={seedingLock}
              productName={draft.productName}
              selectedIds={draft.selectedBloggerIds}
              cartFormats={draft.bloggerFormats}
              onAdd={(id, sel) => addBloggerFormat(id, sel)}
              onRemove={(id, sel) => removeBloggerFormat(id, sel.platform, sel.type)}
            />
          )}
          {branch.bloggersTab === "catalog" && (
            <CatalogSearchTab
              format={branch.campaignType}
              seedingLock={seedingLock}
              selectedIds={draft.selectedBloggerIds}
              cartFormats={draft.bloggerFormats}
              onAdd={(id, sel) => addBloggerFormat(id, sel)}
              onRemove={(id, sel) => removeBloggerFormat(id, sel.platform, sel.type)}
            />
          )}
          {branch.bloggersTab === "adjacent" && (
            <AdjacentTab
              format={branch.campaignType}
              seedingLock={seedingLock}
              productCategory={draft.productCategory}
              selectedIds={draft.selectedBloggerIds}
              cartFormats={draft.bloggerFormats}
              onAdd={(id, sel) => addBloggerFormat(id, sel)}
              onRemove={(id, sel) => removeBloggerFormat(id, sel.platform, sel.type)}
            />
          )}
        </div>

        <BloggersCart />
      </div>
    </div>
  );
}

function CtxItem({ icon, iconBg, iconFg, k, v, sub, big }: {
  icon?: React.ReactNode; iconBg?: string; iconFg?: string;
  k: string; v: string; sub: string; big?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      {icon && (
        <div className="w-9 h-9 grid place-items-center rounded-md flex-shrink-0" style={{ background: iconBg, color: iconFg }}>
          {icon}
        </div>
      )}
      <div>
        <div className="text-[10px] font-display font-semibold uppercase tracking-wider text-ink-40">{k}</div>
        <div className={cn("font-display font-bold leading-tight", big ? "text-[18px] tracking-tight" : "text-[14px]")}>{v}</div>
        <div className="text-[11px] text-ink-40">{sub}</div>
      </div>
    </div>
  );
}

function CatalogSearchTab({ format, seedingLock, selectedIds, cartFormats, onAdd, onRemove }: {
  format: "seeding" | "native";
  seedingLock: boolean;
  selectedIds: string[];
  cartFormats: Record<string, CartSelection[]>;
  onAdd: (id: string, sel: CartSelection) => void;
  onRemove: (id: string, sel: CartSelection) => void;
}) {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<SortKey>("relevance");
  const [history, setHistory] = useState<FilterState[]>([]);

  const { data: rawBloggers = [], isLoading } = useBloggers({ format });
  const allBloggers = seedingLock ? rawBloggers.filter((b) => SEEDING.has(b.platform)) : rawBloggers;

  const commitFilters = (next: FilterState) => {
    setHistory((h) => [...h.slice(-9), filters]);
    setFilters(next);
  };
  const resetAll = () => {
    setHistory((h) => [...h.slice(-9), filters]);
    setFilters(DEFAULT_FILTERS);
  };

  const filtered = sortBloggers(applyFilters(allBloggers, filters), sort);
  const availableNiches = sortNiches([...new Set(allBloggers.flatMap((b) => b.cats))]);
  const platformCounts = facetCounts(allBloggers, filters, "platform", PLATFORMS_META.map((p) => p.id));
  const nicheCounts = facetCounts(allBloggers, filters, "niche", availableNiches);
  const multiPlatformWarning = filters.platform.length !== 1 && (filters.cpvMin !== null || filters.cpvMax !== null);

  return (
    <div className="grid grid-cols-[260px_1fr] gap-5 items-start">
      {/* height accounts for chrome outside <main>'s scroll viewport: 60px
          Topbar + ~61px WizardStepper + ~69px WizardFooterBar, plus the
          top-4 sticky offset and a bottom breathing gap — see WizardStepper.tsx */}
      <aside className="flex flex-col shrink-0 sticky top-4" style={{ height: 'calc(100vh - 230px)' }}>
        <FilterSidebar
          filters={filters} onChange={commitFilters}
          platformCounts={platformCounts} nicheCounts={nicheCounts} availableNiches={availableNiches}
          multiPlatformWarning={multiPlatformWarning} onReset={resetAll}
        />
      </aside>
      <div className="min-w-0">
        <ActiveFilterChips filters={filters} onChange={commitFilters} onResetAll={resetAll} />
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="text-[13px] text-ink-40">
            {isLoading ? "…" : filtered.length === allBloggers.length ? `${filtered.length} блогеров` : `Найдено ${filtered.length}`}
          </div>
          <SortBar sort={sort} onChange={setSort} />
        </div>
        {isLoading ? (
          <div className="py-20 text-center text-ink-40 text-[14px]">Загрузка блогеров…</div>
        ) : filtered.length === 0 ? (
          <EmptyResults canUndo={history.length > 0} onUndo={() => {
            const prev = history[history.length - 1];
            setHistory((h) => h.slice(0, -1));
            setFilters(prev);
          }} />
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((b) => (
              <BloggerCard
                key={b.id}
                blogger={b}
                context="campaign"
                seedingLocked={seedingLock}
                nativeLocked={!seedingLock}
                isInCart={selectedIds.includes(b.id)}
                cartSelections={cartFormats[b.id] ?? []}
                onCartAdd={(sel) => onAdd(b.id, sel)}
                onCartRemove={(sel) => onRemove(b.id, sel)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RecommendationsTab({ seedingLock, selectedIds, cartFormats, onAdd, onRemove }: {
  seedingLock: boolean;
  productName: string;
  selectedIds: string[];
  cartFormats: Record<string, CartSelection[]>;
  onAdd: (id: string, sel: CartSelection) => void;
  onRemove: (id: string, sel: CartSelection) => void;
}) {
  const { data } = useRecommended(seedingLock ? "seeding" : "native");
  const [sort, setSort] = useState<SortKey>("relevance");
  const sorted = sortBloggers((data ?? []).map(r => r.blogger), sort);
  const byId = Object.fromEntries((data ?? []).map(r => [r.blogger.id, r]));

  return (
    <div className="grid gap-2">
      <div className="flex justify-end mb-1">
        <WizardSortBar sort={sort} onChange={setSort} showRelevance />
      </div>
      {sorted.map((b) => {
        const r = byId[b.id];
        return (
          <BloggerCard
            key={b.id}
            blogger={b}
            context="campaign"
            seedingLocked={seedingLock}
            nativeLocked={!seedingLock}
            isInCart={selectedIds.includes(b.id)}
            cartSelections={cartFormats[b.id] ?? []}
            onCartAdd={(sel) => onAdd(b.id, sel)}
            onCartRemove={(sel) => onRemove(b.id, sel)}
            reason={r?.reason}
            reason_type={r?.reason_type}
          />
        );
      })}
    </div>
  );
}

function AdjacentTab({ format, seedingLock, productCategory, selectedIds, cartFormats, onAdd, onRemove }: {
  format: "seeding" | "native";
  seedingLock: boolean;
  productCategory: string;
  selectedIds: string[];
  cartFormats: Record<string, CartSelection[]>;
  onAdd: (id: string, sel: CartSelection) => void;
  onRemove: (id: string, sel: CartSelection) => void;
}) {
  const { data } = useAdjacentBloggers(format, productCategory || undefined);

  if (!productCategory) {
    return (
      <Empty
        icon={<Shuffle size={24} />}
        title="Укажите категорию продукта"
        desc="Выберите категорию на шаге «Продукт» — подберём блогеров из смежных категорий"
      />
    );
  }

  if (data && data.length === 0) {
    return (
      <Empty
        icon={<Shuffle size={24} />}
        title="Нет смежных аудиторий для этой категории"
        desc="Попробуйте поиск по каталогу — там найдутся подходящие каналы"
      />
    );
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-start gap-2 px-3.5 py-2.5 mb-1 rounded-md bg-cream text-[12.5px] text-ink-60 leading-relaxed">
        <Info size={14} className="flex-shrink-0 mt-0.5 text-ink-40" />
        Это не ваша категория напрямую — аудитория пересекается по интересам. На первый взгляд может показаться нерелевантным, но по опыту площадки такие размещения часто конвертируют не хуже прямых попаданий в категорию.
      </div>
      {data?.map((r) => (
        <BloggerCard
          key={r.blogger.id}
          blogger={r.blogger}
          context="campaign"
          seedingLocked={seedingLock}
          nativeLocked={!seedingLock}
          isInCart={selectedIds.includes(r.blogger.id)}
          cartSelections={cartFormats[r.blogger.id] ?? []}
          onCartAdd={(sel) => onAdd(r.blogger.id, sel)}
          onCartRemove={(sel) => onRemove(r.blogger.id, sel)}
          reason={r.reason}
          reason_type="adjacent_affinity"
        />
      ))}
    </div>
  );
}

function Empty({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="grid place-items-center text-center py-14 text-ink-40">
      <div className="mb-2.5">{icon}</div>
      <div className="font-display font-semibold text-ink-60 mb-1">{title}</div>
      <div className="text-[13px]">{desc}</div>
    </div>
  );
}

function BloggersCart() {
  const navigate = useNavigate();
  const ids = useDraftStore((s) => s.draft.selectedBloggerIds);
  const bloggerFormats = useDraftStore((s) => s.draft.bloggerFormats);
  const budget = useDraftStore((s) => s.draft.budget);
  const toggle = useDraftStore((s) => s.toggleBlogger);
  const removeBloggerFormat = useDraftStore((s) => s.removeBloggerFormat);
  const branch = useDraftStore((s) => s.branch);
  const seedingLock = branch.campaignType === "seeding";
  const { data: all } = useBloggers({});

  const items = (all ?? []).filter((b) => ids.includes(b.id));
  const totalRows = items.reduce((s, b) => s + Math.max(1, (bloggerFormats[b.id] ?? []).length), 0);
  const total = items.reduce((s, b) => {
    const fmts = bloggerFormats[b.id] ?? [];
    return s + (fmts.length > 0 ? fmts.reduce((sum, f) => sum + f.price, 0) : b.price);
  }, 0);
  const reach = items.reduce((s, b) => s + b.reach, 0);
  const budgetSet = budget > 0;
  const pct = budgetSet ? Math.min(100, (total / budget) * 100) : 0;
  const over = budgetSet && total > budget;
  const availablePlatforms = seedingLock ? POSEV_PLATFORMS : NATIV_PLATFORMS;

  return (
    <aside className="sticky top-[64px] flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-display font-bold uppercase tracking-[0.16em] text-ink-40">Корзина · {totalRows}</div>
        {items.length > 0 && (
          <button onClick={() => items.forEach((b) => toggle(b.id))}
            className="text-[11px] px-2 py-0.5 text-ink-40 hover:text-ink">Очистить</button>
        )}
      </div>

      <div className="bg-ink text-white rounded-md p-4">
        <div className="text-[10px] font-display font-semibold uppercase tracking-wider text-white/55">Сумма размещений</div>
        <div className={cn("font-display font-extrabold text-[22px] mt-0.5", over && "text-pink")}>{fmtRubFull(total)}</div>
        {budgetSet ? (
          <>
            <div className="mt-3 h-1.5 rounded-pill bg-white/10 overflow-hidden">
              <div className="h-full rounded-pill transition-[width]"
                style={{ width: `${pct}%`, background: over ? "var(--wb-pink)" : "var(--wb-lime)" }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-white/60">
              <span>{over ? "Превышен бюджет" : `из ${fmtRubFull(budget)}`}</span>
              <span>~{fmtNum(reach)} просмотров</span>
            </div>
          </>
        ) : (
          <div className="mt-2 text-[11px] text-white/60">~{fmtNum(reach)} просмотров</div>
        )}
        {items.length > 0 && (() => {
          const totalCpv = total > 0 && reach > 0 ? total / reach : null;
          const totalCpm = total > 0 && reach > 0 ? (total / reach) * 1000 : null;
          return (
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-3 text-[11px] text-white/60">
              {totalCpv !== null && (
                <span className="flex items-center gap-0.5">
                  CPV {totalCpv.toFixed(2)} ₽
                  <InfoTip align="center"><span className="text-white">Cost Per View — средняя стоимость одного просмотра по всем выбранным каналам.</span></InfoTip>
                </span>
              )}
              {totalCpm !== null && (
                <span className="flex items-center gap-0.5">
                  CPM {totalCpm.toFixed(0)} ₽
                  <InfoTip align="center"><span className="text-white">Cost Per Mille — стоимость 1000 просмотров по всем выбранным каналам.</span></InfoTip>
                </span>
              )}
            </div>
          );
        })()}
      </div>

      <div className="grid gap-1.5">
        {items.length === 0 && (
          <div className="grid place-items-center py-6 px-3 text-center text-ink-40">
            <Users size={22} className="mb-2" />
            <div className="text-[12.5px] mb-3">Пусто — выбирайте каналы слева</div>
            <SkipBloggersLink />
          </div>
        )}
        {items.map((b) => {
          const fmts = bloggerFormats[b.id] ?? [];
          const addedPlatforms = fmts.map((f) => f.platform as PlatformKey);
          const missingPlatforms = availablePlatforms.filter((p) => !addedPlatforms.includes(p));
          const showCrossTip = addedPlatforms.length > 0 && missingPlatforms.length > 0;

          if (fmts.length === 0) {
            return (
              <div key={b.id} className="flex items-start gap-2 p-2.5 rounded-md bg-white border border-ink-12">
                <Avatar name={b.full} size={28} grad={b.grad as [string, string]} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 text-[12.5px] font-display font-semibold truncate">
                    <PlatformIcon platform={b.platform as Platform} size={11} /> {b.name}
                  </div>
                  {b.description && (
                    <div className="text-[10.5px] text-ink-60 truncate leading-snug">{b.description}</div>
                  )}
                  <div className="text-[11px] text-ink-40 font-mono">{fmtRub(b.price)}</div>
                </div>
                <button onClick={() => toggle(b.id)}
                  className="w-6 h-6 grid place-items-center rounded-full text-ink-40 hover:text-ink hover:bg-ink-6 flex-shrink-0">
                  <X size={14} />
                </button>
              </div>
            );
          }

          return (
            <div key={b.id} className="rounded-md bg-white border border-ink-12 overflow-hidden">
              <div className="flex items-center gap-2 px-2.5 pt-2 pb-1.5">
                <Avatar name={b.full} size={24} grad={b.grad as [string, string]} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 text-[12.5px] font-display font-semibold truncate">
                    {b.name}
                  </div>
                  {b.description && (
                    <div className="text-[10.5px] text-ink-60 truncate leading-snug">{b.description}</div>
                  )}
                </div>
                {showCrossTip && (
                  <InfoTip align="right">
                    <span className="font-bold flex items-center gap-1 mb-1">
                      <PlusCircle size={11} /> Добавьте в другие соцсети
                    </span>
                    Охватите разную аудиторию, добавив этот канал ещё в{" "}
                    {missingPlatforms.map((p) => PLATFORM_LABELS[p]).join(", ")}.
                  </InfoTip>
                )}
              </div>
              {fmts.map((fmt) => {
                const isPosev = fmt.type === "posev";
                const typeColor = isPosev ? { bg: "#EAF3DE", text: "#27500A" } : { bg: "#E6F1FB", text: "#185FA5" };
                return (
                  <div key={`${fmt.platform}::${fmt.type}`}
                    className="flex items-center gap-2 px-2.5 py-1 border-t border-ink-12/50">
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex h-4 px-1.5 rounded-full text-[9px] font-display font-bold"
                        style={{ background: typeColor.bg, color: typeColor.text }}>
                        {isPosev ? "Посев" : "Натив"} · {fmt.formatName} · {PLATFORM_LABELS[fmt.platform as PlatformKey]}
                      </span>
                      <span className="text-[11px] font-mono text-ink-40">{fmtRub(fmt.price)}</span>
                    </div>
                    <button
                      onClick={() => removeBloggerFormat(b.id, fmt.platform as PlatformKey, fmt.type)}
                      className="w-5 h-5 grid place-items-center rounded-full text-ink-40 hover:text-ink hover:bg-ink-6 flex-shrink-0">
                      <X size={11} />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {items.length > 0 && (
        <button
          onClick={() => navigate("/campaign/new/creative")}
          className="w-full h-10 rounded-lg bg-lime text-[var(--fg-on-accent)] text-[13px] font-display font-semibold hover:brightness-105 transition-[filter] flex items-center justify-center gap-1.5"
        >
          К креативу →
        </button>
      )}
    </aside>
  );
}

function SkipBloggersLink() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/campaign/new/creative")}
      className="text-[12px] text-link hover:underline">
      Пропустить и перейти к креативу →
    </button>
  );
}

type WizardSortField = "reach" | "price" | "cpv";
const WIZARD_SORT_FIELDS: { field: WizardSortField; label: string; defaultDir: "asc" | "desc" }[] = [
  { field: "reach", label: "Охват",    defaultDir: "desc" },
  { field: "price", label: "Цена",     defaultDir: "asc"  },
  { field: "cpv",   label: "CPV",      defaultDir: "asc"  },
];

function WizardSortBar({ sort, onChange, showRelevance }: { sort: SortKey; onChange: (s: SortKey) => void; showRelevance?: boolean }) {
  const activeField = sort === "relevance" ? null : (sort.split("_")[0] as WizardSortField);
  const activeDir   = sort === "relevance" ? null : (sort.split("_")[1] as "asc" | "desc");

  function handleClick(field: WizardSortField, defaultDir: "asc" | "desc") {
    if (activeField === field) {
      onChange(`${field}_${activeDir === "asc" ? "desc" : "asc"}` as SortKey);
    } else {
      onChange(`${field}_${defaultDir}` as SortKey);
    }
  }

  return (
    <div className="ml-auto inline-flex items-center gap-0.5 p-0.5 rounded-pill bg-ink-6">
      {showRelevance && (
        <button
          onClick={() => onChange("relevance")}
          className={cn(
            "h-7 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors",
            sort === "relevance" ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink",
          )}
        >
          По релевантности
        </button>
      )}
      {WIZARD_SORT_FIELDS.map(({ field, label, defaultDir }) => {
        const active = activeField === field;
        const dir = active ? activeDir : defaultDir;
        return (
          <button
            key={field}
            onClick={() => handleClick(field, defaultDir)}
            className={cn(
              "h-7 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors inline-flex items-center gap-1",
              active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink",
            )}
          >
            {label}
            <span className="text-[10px] leading-none">{dir === "asc" ? "↑" : "↓"}</span>
          </button>
        );
      })}
    </div>
  );
}

function BudgetCtxItem({ budget }: { budget: number }) {
  const [open, setOpen] = useState(false);
  if (budget > 0) {
    return (
      <>
        <button onClick={() => setOpen(true)} className="text-left">
          <CtxItem k="Бюджет" v={fmtRubFull(budget)} sub="Изменить →" big />
        </button>
        {open && <BudgetModal onClose={() => setOpen(false)} />}
      </>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 grid place-items-center rounded-md bg-cream text-ink-60 flex-shrink-0">
          <Wallet size={15} />
        </div>
        <div>
          <div className="text-[10px] font-display font-semibold uppercase tracking-wider text-ink-40">Бюджет</div>
          <div className="font-display font-bold text-[14px] leading-tight">Не указан</div>
          <button onClick={() => setOpen(true)} className="text-[11.5px] text-link hover:underline">
            Как определить бюджет?
          </button>
        </div>
      </div>
      {open && <BudgetModal onClose={() => setOpen(false)} />}
    </>
  );
}

function BudgetModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center p-6"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg fade-up flex flex-col overflow-hidden"
        style={{ width: "min(960px, 96vw)", height: "min(90vh, 820px)" }}>
        <div className="flex items-center justify-between p-6 border-b border-ink-12 flex-shrink-0">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">Подсказка</div>
            <h3 className="font-display font-bold text-[20px] tracking-tight">Как определить бюджет</h3>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={onClose}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
              Сохранить
            </button>
            <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6"><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 p-6">
          <BudgetPicker />
        </div>
      </div>
    </div>
  );
}

